import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Dispatch, JSX, SetStateAction, useState } from "react";
import { useForm, SubmitHandler, Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TAddCarSchema, AddCarSchema, AddCarDefaultValues } from "../Models";
import { useAddCarMutationHook } from "@/hooks";
import { useFetchServicesOnTenderQueryHook } from "@/hooks/queries";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormattedNumberInputField, InputField } from "@/components/shared";
import { CircleLoader } from "@/components/shared/loaders";
import { prefillAddCarFormWithMockData } from "./mocks";
import ServiceRatioSelect from "../Components/ServiceRatioSelect";
import StepIndicator from "../Components/StepIndicator";
import { ServiceRatioInput, ServiceType } from "@/graphql/__generated__/graphql";
import { convertDecimalToBp } from "@/utils/helpers";
import { Form } from "@/components/ui/form";

interface Props {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}

/**
 * AddCar form component with stepper flow
 * Step 1: Car details
 * Step 2: Link to services
 *
 * @param {Props} props
 * @returns {JSX.Element}
 */
const AddCar = (props: Props): JSX.Element => {
  const { open, onOpenChange } = props;

  // Stepper state
  const [currentStep, setCurrentStep] = useState(1);

  // Init form object
  const form = useForm<TAddCarSchema>({
    resolver: zodResolver(AddCarSchema) as Resolver<TAddCarSchema>,
    mode: "onChange",
    defaultValues: AddCarDefaultValues,
  });

  // State for service ratios
  const [selectedServices, setSelectedServices] = useState<ServiceRatioInput[]>([]);

  // Hooks
  const { t } = useTranslation(["modals", "common", "translation"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  // Query hooks
  const { data: servicesData, loading: servicesLoading } = useFetchServicesOnTenderQueryHook(Number(tenderID), false, true);

  // Mutation hooks
  const { addCarMutation, loading: addCarLoading } = useAddCarMutationHook();

  // Check if in development mode
  const isDevMode = import.meta.env.VITE_APP_ENV === "dev";

  // Mock data function for testing
  const handlePrefillMockData = () => {
    prefillAddCarFormWithMockData({ setValue: form.setValue, trigger: form.trigger });
  };

  // Get services for the dropdown (only permanent services, as mobile services cannot have cars)
  const availableServices =
    servicesData?.fetchServicesOnTender?.services
      ?.filter((service) => service.type !== ServiceType.Mobile && service.additional === false)
      .map((service) => ({
        id: service.id,
        name: service.name,
        operationalOrganizationShortName: service.operationalOrganizationShortName,
      })) ?? [];

  // Calculate if service ratios are valid (either empty or sum to 100)
  const totalRatio = selectedServices.reduce((sum, s) => sum + s.ratio, 0);
  const isRatioValid = selectedServices.length === 0 || totalRatio === 100;

  const inputFieldsData = [
    {
      name: "netValue",
      label: t("addcar.inputs.netvalue.label"),
      placeholder: t("addcar.inputs.netvalue.placeholder"),
      style: "currency",
    },
    {
      name: "runtime",
      label: t("addcar.inputs.runtime.label"),
      placeholder: t("addcar.inputs.runtime.placeholder"),
      isInteger: true,
    },
    {
      name: "yearlyInsurance",
      label: t("addcar.inputs.yearlyinsurance.label"),
      placeholder: t("addcar.inputs.yearlyinsurance.placeholder"),
      style: "currency",
    },
    {
      name: "yearlyTax",
      label: t("addcar.inputs.yearlytax.label"),
      placeholder: t("addcar.inputs.yearlytax.placeholder"),
      style: "currency",
    },
    {
      name: "yearlyKilometers",
      label: t("addcar.inputs.yearlykilometers.label"),
      placeholder: t("addcar.inputs.yearlykilometers.placeholder"),
    },
    {
      name: "consumption",
      label: t("addcar.inputs.consumption.label"),
      placeholder: t("addcar.inputs.consumption.placeholder"),
    },
    {
      name: "fuelPrice",
      label: t("addcar.inputs.fuelprice.label"),
      placeholder: t("addcar.inputs.fuelprice.placeholder"),
      style: "currency",
      additionalSuffix: " / L",
    },
    {
      name: "repairPerKilometer",
      label: t("addcar.inputs.repairperkm.label"),
      placeholder: t("addcar.inputs.repairperkm.placeholder"),
      style: "currency",
      additionalSuffix: " / km",
    },
    {
      name: "quantity",
      label: t("addcar.inputs.quantity.label"),
      placeholder: t("addcar.inputs.quantity.placeholder"),
      isInteger: true,
    },
    {
      name: "margin",
      label: t("addcar.inputs.margin.label"),
      placeholder: t("addcar.inputs.margin.placeholder"),
      additionalSuffix: "%",
    },
  ];

  /**
   * Handle navigation to next step
   */
  const handleNext = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (currentStep === 1) {
      // Validate step 1 (form fields)
      const isValid = await form.trigger();
      if (isValid) {
        setCurrentStep(2);
      }
    }
  };

  /**
   * Handle navigation to previous step
   */
  const handleBack = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  /**
   * Handler function for adding a car to a tender
   * @param {TAddCarSchema} data
   */
  const onSubmit: SubmitHandler<TAddCarSchema> = (data: TAddCarSchema) => {
    // Validate service ratios
    if (!isRatioValid) {
      return;
    }

    // Clear errors
    form.clearErrors();

    // Convert margin from percentage to basis points
    const inputData = {
      ...data,
      margin: convertDecimalToBp(data.margin),
    };

    // Convert service ratios from percentage (0-100) to basis points (0-10000)
    const servicesInBp = selectedServices.map((s) => ({
      ...s,
      ratio: convertDecimalToBp(s.ratio),
    }));

    addCarMutation({
      variables: {
        tenderId: Number(tenderID),
        linkedServices: servicesInBp,
        input: inputData,
      },
    }).then((res) => {
      // Only proceed if there are no errors
      if (!res.errors) {
        // Clear errors
        form.clearErrors();
        // Reset form
        form.reset(AddCarDefaultValues);
        // Reset service selection
        setSelectedServices([]);
        // Reset to step 1
        setCurrentStep(1);

        // Close modal
        onOpenChange(false);
      }
    });
  };

  // Reset state when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedServices([]);
      setCurrentStep(1);
      form.reset(AddCarDefaultValues);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[90vw] lg:max-w-2xl overflow-y-auto max-h-[90vh] h-full md:h-auto">
        <Form {...form}>
          <form className="space-y-6" id="add-car-form" onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>{t("addcar.title")}</DialogTitle>
              <DialogDescription>{t("addcar.description")}</DialogDescription>
            </DialogHeader>

            {/* Step Indicator */}
            <StepIndicator currentStep={currentStep} totalSteps={2} />

            {/* Step 1: Car Details */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground">
                  {t("translation:stepper.step1description")}
                </h3>
                <div className="flex-wrap-4">
                  <div className="form-container">
                    <InputField
                      form={form}
                      name="name"
                      label={t("addcar.inputs.name.label")}
                      placeholder={t("addcar.inputs.name.placeholder")}
                      className="w-full"
                    />
                  </div>
                  {inputFieldsData.map((fieldData) => (
                    <div key={fieldData.name} className="form-container">
                      <FormattedNumberInputField
                        name={fieldData.name}
                        control={form.control}
                        additionalSuffix={fieldData.additionalSuffix}
                        placeholder={fieldData.placeholder}
                        style={fieldData.style as "currency" | "decimal"}
                        isInteger={fieldData.isInteger}
                        isStandalone={true}
                        label={fieldData.label}
                        className="w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Link to Services */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground">
                  {t("translation:stepper.step2description")}
                </h3>
                <ServiceRatioSelect
                  services={availableServices}
                  selectedServices={selectedServices}
                  onServicesChange={setSelectedServices}
                  disabled={addCarLoading}
                  loading={servicesLoading}
                />
              </div>
            )}

            {/* Navigation Buttons */}
            <DialogFooter className="flex justify-between sm:justify-between">
              <div className="flex gap-2">
                {isDevMode && currentStep === 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrefillMockData}
                    disabled={addCarLoading}
                  >
                  🧪 Mock Fill
                  </Button>
                )}
                {currentStep > 1 && (
                  <Button type="button" variant="outline" onClick={handleBack}>
                    {t("common:buttons.back")}
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={addCarLoading}
                >
                  {t("common:buttons.cancel")}
                </Button>
                {currentStep < 2 ? (
                  <Button type="button" onClick={handleNext}>
                    {t("common:buttons.next")}
                  </Button>
                ) : (
                  <Button type="submit" disabled={addCarLoading || !isRatioValid} className="gap-2">
                    <CircleLoader visible={addCarLoading} height={16} width={16} strokeWidth={2} />
                    {t("common:buttons.submit")}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddCar;
